
import React, { useState, useRef, useEffect, useCallback } from 'react';
import FlowsComponent from '../../components/Flows/FlowsComponent';
import { IntegrationFlow, FlowNode, Language, User } from '../../types';
import { FlowsService } from '../../api';
import { useDataTemplate } from '../../hooks/useDataTemplate';

interface FlowsTemplateProps {
  language: Language;
  user: User;
  flows: IntegrationFlow[]; // Deprecated: Kept for compat, but internal fetch preferred
  onAddFlow: (flow: Omit<IntegrationFlow, 'id'>) => void; // Deprecated
  onDeleteFlow: (id: string) => void; // Deprecated
  onToggleFlow: (id: string) => void;
}

const FlowsTemplate: React.FC<FlowsTemplateProps> = (props) => {
  // --- Data Gateway ---
  const { data: fetchedFlows, setData: setFlows, refresh } = useDataTemplate<IntegrationFlow[]>(FlowsService.getAll);
  
  // Local UI State
  const [viewMode, setViewMode] = useState<'list' | 'editor'>('list');
  const [activeFlow, setActiveFlow] = useState<IntegrationFlow | null>(null);
  
  // Editor Canvas State
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [isLibraryOpen, setIsLibraryOpen] = useState(true);
  const [libraryTab, setLibraryTab] = useState<'nodes' | 'services'>('nodes');
  const [showInspector, setShowInspector] = useState(false);
  const [isSimulating, setIsSimulating] = useState(false);
  
  // Dragging State
  const [isDragging, setIsDragging] = useState(false);
  const [dragNodeId, setDragNodeId] = useState<string | null>(null);
  const [connectionStart, setConnectionStart] = useState<string | null>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const canvasRef = useRef<HTMLDivElement>(null);

  // Sync to local storage for persistence in Local Mode (simulating DB write)
  useEffect(() => {
    if (fetchedFlows) localStorage.setItem('lahja_flows', JSON.stringify(fetchedFlows));
  }, [fetchedFlows]);

  const handleCreateFlow = () => {
    const newFlow: IntegrationFlow = {
      id: Date.now().toString(),
      name: props.language === 'ar' ? 'تدفق جديد' : 'Untitled Flow',
      active: false,
      nodes: [{ id: 'start-1', type: 'trigger', position: { x: 100, y: 300 }, data: { label: 'Start', subLabel: 'Trigger', icon: 'fa-bolt', config: { type: 'webhook' } } }],
      edges: []
    };
    handleOpenFlow(newFlow);
  };

  const handleOpenFlow = (flow: IntegrationFlow) => {
    setActiveFlow(JSON.parse(JSON.stringify(flow))); // Deep copy
    setViewMode('editor');
    setPan({ x: 0, y: 0 });
    setZoom(1);
  };

  const handleSave = async () => {
    if (activeFlow) {
      if (fetchedFlows?.find(f => f.id === activeFlow.id)) {
          // Update
          await FlowsService.save(activeFlow);
          setFlows(prev => prev?.map(f => f.id === activeFlow.id ? activeFlow : f) || []);
      } else {
          // Create
          await FlowsService.create(activeFlow);
          setFlows(prev => [...(prev || []), activeFlow]);
      }
      setViewMode('list');
    }
  };

  const handleDeleteFlow = async (id: string) => {
      if (window.confirm('Delete this flow?')) {
          await FlowsService.delete(id);
          setFlows(prev => prev?.filter(f => f.id !== id) || []);
      }
  };

  // Node Logic
  const handleAddNode = (def: any, isService: boolean) => {
    if (!activeFlow || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const centerX = (-pan.x + rect.width / 2) / zoom - (260 / 2);
    const centerY = (-pan.y + rect.height / 2) / zoom - (100 / 2);

    const newNode: FlowNode = {
      id: `${isService ? 'svc' : def.type}_${Date.now()}`,
      type: isService ? 'ai_service' : def.type,
      position: { x: centerX, y: centerY },
      data: {
        label: def.title || def.label,
        subLabel: isService ? 'AI Service' : def.cat,
        icon: isService ? def.icon : def.icon || 'fa-cube',
        color: isService ? 'emerald' : def.color || 'slate',
        config: isService ? { serviceId: def.id, type: 'ai_service' } : { type: def.type }
      }
    };
    setActiveFlow({ ...activeFlow, nodes: [...activeFlow.nodes, newNode] });
  };

  const handleDeleteNode = (id: string) => {
    if (!activeFlow) return;
    setActiveFlow({
      ...activeFlow,
      nodes: activeFlow.nodes.filter(n => n.id !== id),
      edges: activeFlow.edges.filter(e => e.source !== id && e.target !== id)
    });
    setSelectedNodeId(null);
    setShowInspector(false);
  };

  // Canvas Interactions
  const handleCanvasMouseDown = (e: React.MouseEvent | React.TouchEvent) => {
    if ((e.target as HTMLElement).closest('.flow-interactive')) return;
    setIsDragging(true);
    const clientX = 'touches' in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : (e as React.MouseEvent).clientY;
    setMousePos({ x: clientX, y: clientY });
    setSelectedNodeId(null);
    setShowInspector(false);
  };

  const handleMouseMove = useCallback((e: MouseEvent | TouchEvent) => {
    const clientX = 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : (e as MouseEvent).clientY;

    if (isDragging) {
      const dx = clientX - mousePos.x;
      const dy = clientY - mousePos.y;
      setPan(p => ({ x: p.x + dx, y: p.y + dy }));
      setMousePos({ x: clientX, y: clientY });
    } else if (dragNodeId && activeFlow) {
      const dx = (clientX - mousePos.x) / zoom;
      const dy = (clientY - mousePos.y) / zoom;
      setActiveFlow(prev => prev ? {
        ...prev,
        nodes: prev.nodes.map(n => n.id === dragNodeId ? { ...n, position: { x: n.position.x + dx, y: n.position.y + dy } } : n)
      } : null);
      setMousePos({ x: clientX, y: clientY });
    } else if (connectionStart) {
        setMousePos({ x: clientX, y: clientY });
    }
  }, [isDragging, dragNodeId, activeFlow, mousePos, zoom, connectionStart]);

  const handleMouseUp = () => {
    setIsDragging(false);
    setDragNodeId(null);
    setConnectionStart(null);
  };

  useEffect(() => {
    if (viewMode === 'editor') {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleMouseMove, { passive: false });
      window.addEventListener('touchend', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('touchmove', handleMouseMove);
        window.removeEventListener('touchend', handleMouseUp);
      };
    }
  }, [viewMode, handleMouseMove]);

  const handlePortMouseUp = (e: React.MouseEvent, nodeId: string) => {
     e.stopPropagation(); 
     if (connectionStart && connectionStart !== nodeId) {
        if (!activeFlow?.edges.find(edge => edge.source === connectionStart && edge.target === nodeId)) {
            setActiveFlow(prev => prev ? {
                ...prev,
                edges: [...prev.edges, { id: `e_${Date.now()}`, source: connectionStart, target: nodeId }]
            } : null);
        }
     }
     setConnectionStart(null);
  };

  const handleUpdateNodeLabel = (val: string) => {
    if (selectedNodeId && activeFlow) {
        setActiveFlow({
            ...activeFlow,
            nodes: activeFlow.nodes.map(n => n.id === selectedNodeId ? { ...n, data: { ...n.data, label: val } } : n)
        });
    }
  };

  const handleUpdateNodeConfig = (key: string, value: any) => {
    if (selectedNodeId && activeFlow) {
        setActiveFlow({
            ...activeFlow,
            nodes: activeFlow.nodes.map(n => n.id === selectedNodeId ? { 
                ...n, 
                data: { 
                    ...n.data, 
                    config: { ...n.data.config, [key]: value } 
                } 
            } : n)
        });
    }
  };

  if (!fetchedFlows) return <div className="flex items-center justify-center h-full text-slate-500"><i className="fas fa-spinner fa-spin mr-2"></i> Loading Flows...</div>;

  return (
    <FlowsComponent
      language={props.language}
      flows={fetchedFlows}
      activeFlow={activeFlow}
      viewMode={viewMode}
      isLibraryOpen={isLibraryOpen}
      libraryTab={libraryTab}
      isSimulating={isSimulating}
      pan={pan}
      zoom={zoom}
      selectedNodeId={selectedNodeId}
      showInspector={showInspector}
      connectionStart={connectionStart}
      mousePos={mousePos}
      canvasRef={canvasRef}
      onAddFlow={handleCreateFlow}
      onOpenFlow={handleOpenFlow}
      onSave={handleSave}
      onDeleteFlow={handleDeleteFlow}
      onAutoLayout={() => {}} 
      onToggleSimulation={() => setIsSimulating(!isSimulating)}
      onUpdateFlowName={(name) => setActiveFlow(prev => prev ? { ...prev, name } : null)}
      onSetViewMode={setViewMode}
      onSetLibraryTab={setLibraryTab}
      onAddNode={handleAddNode}
      onDeleteNode={handleDeleteNode}
      onNodeMouseDown={(e, id) => { e.stopPropagation(); setDragNodeId(id); setSelectedNodeId(id); setShowInspector(true); setMousePos({x: e.clientX, y: e.clientY}); }}
      onCanvasMouseDown={handleCanvasMouseDown}
      onPortMouseDown={(e, id) => { e.stopPropagation(); setConnectionStart(id); }}
      onPortMouseUp={handlePortMouseUp}
      onUpdateNodeLabel={handleUpdateNodeLabel}
      onUpdateNodeConfig={handleUpdateNodeConfig}
      onCloseInspector={() => setShowInspector(false)}
      onZoom={(delta) => setZoom(z => Math.max(0.5, Math.min(2, z + delta)))}
      onResetView={() => { setPan({x:0, y:0}); setZoom(1); }}
    />
  );
};

export default FlowsTemplate;
